import { useEffect, useState } from 'react';
import { LOADING_TYPES } from '@/shared/config';
import { useReportStore } from '@/entities/report';
import { useLoadingStore } from '@/shared/lib/loadingStore';
import { toggleLoadingCursor } from '@/shared/lib/browser';

/**
 * Drives the full-page loading overlay, tracking the current loading text and which report is being generated. Mirrors the store loading text into local state in an effect, toggling the busy cursor on and off and clearing it on unmount, then splits a Rendering report status into clinic and AIRP generation on the isRenderingBackground flag.
 *
 * @returns An object carrying isLoadingInitial, loadingText, isGeneratingClinicReport, isGeneratingAirpReport and isGeneratingReport.
 */
const useLoadingPage = () => {
  const loading = useLoadingStore((s) => s.loading);

  const [loadingText, setLoadingText] = useState('');
  const reportData = useReportStore((s) => s.reportData);

  useEffect(() => {
    if (loading.loadingText !== loadingText) {
      if (loading.loadingText) {
        toggleLoadingCursor(true);
      } else {
        toggleLoadingCursor(false);
      }
      setLoadingText(loading.loadingText);
    }

    // *: Hide loading cursor when navigating to new route (e.g. from Login page to inside pages)
    return () => {
      toggleLoadingCursor(false);
    };
  }, [loading]);

  const { isRenderingBackground, status: reportStatus } = reportData?.report || {};
  const isRendering = reportStatus === 'Rendering';
  const isGeneratingClinicReport = isRendering && isRenderingBackground === true;
  const isGeneratingAirpReport = isRendering && isRenderingBackground === false;

  const isGeneratingReport =
    loadingText === LOADING_TYPES.GENERATE_REPORT || isGeneratingAirpReport;

  return {
    isLoadingInitial: loading?.isLoadingInitial,
    loadingText,
    isGeneratingClinicReport,
    isGeneratingAirpReport,
    isGeneratingReport,
  };
};

export default useLoadingPage;
